/**
 * Typed wrappers around the gateway routes this app uses. Shapes come from
 * `@cms/contracts` (see packages/contracts/src/index.ts) and from reading
 * `apps/gateway/src/routes/*.ts` for the exact path and query parameters —
 * the contracts file describes the service interface, not the HTTP wire
 * shape, so both were needed. No endpoint here is invented: every path
 * matches document/25-api-e-mcp.md §3.
 */
import type {
  AccessibleTenant,
  ContentDetail,
  ContentSummary,
  ContentTypeDefinition,
  CompanyKnowledgeDetail,
  Page,
  PageDetail,
  PageSectionNode,
  PageSummary,
  ProjectTree,
  RepositoryMappingAggregateType,
  RepositoryMappingDetail,
  PublicationSummary,
  RegisterTenantMemberRequest,
  ReplaceProjectGrantRequest,
  SetTenantMemberStatusRequest,
  SiteDetail,
  SiteDeliveryStatus,
  SiteListing,
  SiteSummary,
  ThemeDetail,
  ThemeLayout,
  ThemeSummary,
  TenantMemberSummary,
  WriteRepositoryMappingRequest,
  WriteResult,
  MainImageRef,
} from '@cms/contracts'
import { apiFetch } from './client.js'

export type MenuTarget =
  | { kind: 'reference'; contentId: string }
  | { kind: 'external'; url: string }
  | { kind: 'query'; contentTypeKey: string; locale?: string; limit?: number }

export interface MenuItemDto {
  id: string
  label: Record<string, string>
  target: MenuTarget
  visibility: 'always' | 'desktop' | 'mobile' | `audience:${string}`
  style: 'normal' | 'featured' | 'callToAction'
  children: MenuItemDto[]
  /** Absent: the original nested list. `mega` needs `columns`. */
  display?: 'dropdown' | 'mega'
  columns?: MenuColumnDto[]
}

export interface MenuColumnDto {
  id: string
  title: Record<string, string>
  items: MenuItemDto[]
  highlight?: MenuHighlightDto
}

export interface MenuHighlightDto {
  title: Record<string, string>
  description?: Record<string, string>
  cta: Record<string, string>
  target: MenuTarget
}

export interface MenuWarningDto {
  itemId: string
  code: 'target_missing' | 'page_not_published' | 'page_trashed' | 'page_scheduled'
  message: string
}

export interface MenuPreviewDto {
  locale: string
  path: string
  html: Record<string, string>
  css: string
  warnings: Record<string, MenuWarningDto[]>
}

export interface SiteMenuDto {
  id: string
  version: number
  siteId: string
  key: string
  items: MenuItemDto[]
}

export interface SectionDefinitionDto {
  key: string
  label?: Record<string, string> | string
  description?: Record<string, string> | string
  variants?: string[]
  props?: unknown
  [key: string]: unknown
}

export interface ProvisionResultDto {
  tenantId: string
  projectId: string
  siteId: string
  themeId: string
  pages: { slug: string; id: string; layoutId: string }[]
  menuId: string
  previewUrl: string
  nextSteps: string[]
}

export interface RecordedEventDto {
  readonly aggregateId: string
  readonly seq: number
  readonly type: string
  readonly payload: Record<string, unknown>
  readonly reason: string
  readonly actorId: string
  readonly actorKind: string
  readonly ip: string | null
  readonly userAgent: string | null
  readonly device: string | null
  readonly occurredAt: number
}

export interface PublicationStatus {
  readonly id: string
  readonly siteId: string
  readonly status: 'inProgress' | 'succeeded' | 'failed'
  readonly failureReason: string | null
  readonly phase: 'planning' | 'rendering' | 'deploying' | 'completed' | 'failed'
  readonly totalFiles: number
  readonly completedFiles: number
  readonly deploymentUrl: string | null
  /** What the run had to work around; empty when it went cleanly. */
  readonly warnings: string[]
  readonly [key: string]: unknown
}

/** Every write shares this envelope: `reason` and `expectedVersion` are mandatory (doc 25 §3). */
export interface WriteEnvelope {
  reason: string
  expectedVersion: number
}

export function listSites(query: { projectId?: string; limit?: number; cursor?: string } = {}): Promise<Page<SiteSummary>> {
  return apiFetch('/sites', { query })
}

/**
 * The tenants this person can pick with `X-Tenant` (apps/gateway/src/routes/tenants.ts):
 * home, memberships, and for a platform operator every other one, listed but not selectable.
 */
export function listTenants(): Promise<AccessibleTenant[]> {
  return apiFetch('/tenants')
}

export function getCompanyKnowledge(tenantId: string): Promise<CompanyKnowledgeDetail> {
  return apiFetch(`/tenants/${tenantId}/knowledge/detail`)
}

export function setCompanyKnowledge(
  tenantId: string,
  body: WriteEnvelope & { path: string; value: unknown },
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/tenants/${tenantId}/knowledge`, { method: 'POST', body, idempotencyKey })
}

export function removeCompanyKnowledge(
  tenantId: string,
  body: WriteEnvelope & { path: string },
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/tenants/${tenantId}/knowledge`, { method: 'DELETE', body, idempotencyKey })
}

export interface ListTenantMembersQuery {
  limit?: number
  cursor?: string
  status?: TenantMemberSummary['status']
  q?: string
}

export function listTenantMembers(tenantId: string, query: ListTenantMembersQuery = {}): Promise<Page<TenantMemberSummary>> {
  return apiFetch(`/tenants/${tenantId}/members`, { query: query as Record<string, string | number | undefined> })
}

export function getTenantMember(tenantId: string, personId: string): Promise<TenantMemberSummary> {
  return apiFetch(`/tenants/${tenantId}/members/${personId}`)
}

export function registerTenantMember(
  tenantId: string,
  body: RegisterTenantMemberRequest,
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/tenants/${tenantId}/members`, { method: 'POST', body, idempotencyKey })
}

export function replaceProjectGrant(
  tenantId: string,
  personId: string,
  projectId: string,
  body: ReplaceProjectGrantRequest,
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/tenants/${tenantId}/members/${personId}/projects/${projectId}`, { method: 'PUT', body, idempotencyKey })
}

export function removeProjectGrant(
  tenantId: string,
  personId: string,
  projectId: string,
  body: WriteEnvelope,
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/tenants/${tenantId}/members/${personId}/projects/${projectId}`, { method: 'DELETE', body, idempotencyKey })
}

export function setTenantMemberStatus(
  tenantId: string,
  personId: string,
  body: SetTenantMemberStatusRequest,
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/tenants/${tenantId}/members/${personId}/status`, { method: 'PUT', body, idempotencyKey })
}

export function getProjectTree(): Promise<ProjectTree> {
  return apiFetch('/projects/tree')
}

const repositoryMappingRoutes: Record<RepositoryMappingAggregateType, string> = {
  Content: 'contents',
  Page: 'pages',
  Menu: 'menus',
  Theme: 'themes',
}

export function getRepositoryMapping<T extends RepositoryMappingAggregateType>(
  aggregateType: T,
  id: string,
): Promise<RepositoryMappingDetail<T>> {
  return apiFetch(`/${repositoryMappingRoutes[aggregateType]}/${encodeURIComponent(id)}/repository-mapping`)
}

export function setRepositoryMapping<T extends RepositoryMappingAggregateType>(
  aggregateType: T,
  id: string,
  body: WriteRepositoryMappingRequest<T>,
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/${repositoryMappingRoutes[aggregateType]}/${encodeURIComponent(id)}/repository-mapping`, {
    method: 'PUT', body, idempotencyKey,
  })
}

export function getSite(id: string): Promise<SiteDetail> {
  return apiFetch(`/sites/${id}`)
}

export function addSiteDomain(
  id: string,
  body: WriteEnvelope & { domain: string; primary?: boolean },
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/sites/${id}/domains`, { method: 'POST', body, idempotencyKey })
}

export function attachSiteRepository(
  id: string,
  body: WriteEnvelope & { url: string; branch: string },
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/sites/${id}/repository`, { method: 'POST', body, idempotencyKey })
}

export function setSiteLocales(
  id: string,
  body: WriteEnvelope & { locales: string[]; defaultLocale: string },
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/sites/${id}/locales`, { method: 'PUT', body, idempotencyKey })
}

export function setSiteProfile(
  id: string,
  body: WriteEnvelope & { publicName: string; description: string },
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/sites/${id}/profile`, { method: 'PUT', body, idempotencyKey })
}

/** `version` is the site's: listing writes are Site commands and carry it as `expectedVersion`. */
export interface SiteListingsDto {
  siteId: string
  version: number
  publicName: string
  description: string
  items: SiteListing[]
  total: number
}

export function listSiteListings(siteId: string): Promise<SiteListingsDto> {
  return apiFetch(`/sites/${siteId}/listings`)
}

export function saveSiteListing(
  siteId: string,
  key: string,
  body: WriteEnvelope & Omit<SiteListing, 'key'>,
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/sites/${siteId}/listings/${encodeURIComponent(key)}`, { method: 'PUT', body, idempotencyKey })
}

export function removeSiteListing(siteId: string, key: string, body: WriteEnvelope, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/sites/${siteId}/listings/${encodeURIComponent(key)}`, { method: 'DELETE', body, idempotencyKey })
}

export function getSiteDelivery(id: string): Promise<SiteDeliveryStatus> {
  return apiFetch(`/sites/${id}/delivery`)
}

export function setSiteDelivery(
  id: string,
  body: WriteEnvelope & { provider: 'cloudflare-pages'; enabled: boolean },
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/sites/${id}/delivery`, { method: 'PUT', body, idempotencyKey })
}

export function listSiteMenus(siteId: string): Promise<SiteMenuDto[]> {
  return apiFetch(`/sites/${siteId}/menus`)
}

export function saveSiteMenu(
  siteId: string,
  body: WriteEnvelope & { key: string; items: MenuItemDto[] },
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/sites/${siteId}/menus`, { method: 'POST', body, idempotencyKey })
}

/** What the public site draws for these (possibly unsaved) items, and what it could not link to. Writes nothing. */
export function previewSiteMenu(
  siteId: string,
  body: { key: string; items: MenuItemDto[]; locale: string; path?: string },
): Promise<MenuPreviewDto> {
  return apiFetch(`/sites/${siteId}/menus/preview`, { method: 'POST', body })
}

export function getTheme(themeId: string): Promise<ThemeDetail> {
  return apiFetch(`/themes/${themeId}`)
}

export function listSiteThemes(siteId: string, cursor?: string): Promise<Page<ThemeSummary>> {
  return apiFetch(`/sites/${siteId}/themes`, { query: { limit: 100, cursor } })
}

export function createTheme(siteId: string, body: WriteEnvelope & { name: string; derivedFrom?: string; activate?: boolean }, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/sites/${siteId}/theme`, { method: 'POST', body, idempotencyKey })
}

export function activateTheme(siteId: string, themeId: string, body: WriteEnvelope, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/sites/${siteId}/theme/${themeId}/activate`, { method: 'POST', body, idempotencyKey })
}

export function addThemeVariant(themeId: string, body: WriteEnvelope & { name: string }, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/themes/${themeId}/variants`, { method: 'POST', body, idempotencyKey })
}

export function setThemeToken(
  themeId: string,
  body: WriteEnvelope & { name: string; value: string; kind: string; variant?: string },
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/themes/${themeId}/tokens`, { method: 'PATCH', body, idempotencyKey })
}

export function setThemeCss(
  themeId: string,
  body: WriteEnvelope & { css: string },
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/themes/${themeId}/css`, { method: 'PATCH', body, idempotencyKey })
}

/** `expectedVersion` is the theme's version; a language without a frame borrows the default language one. */
export function setThemeLayout(
  themeId: string,
  locale: string,
  body: WriteEnvelope & ThemeLayout,
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/themes/${themeId}/layouts/${encodeURIComponent(locale)}`, { method: 'PUT', body, idempotencyKey })
}

export function removeThemeLayout(themeId: string, locale: string, body: WriteEnvelope, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/themes/${themeId}/layouts/${encodeURIComponent(locale)}`, { method: 'DELETE', body, idempotencyKey })
}

export function listSections(): Promise<SectionDefinitionDto[]> {
  return apiFetch('/sections')
}

/**
 * What the gateway adds to a page since the lifecycle (document/62, area 2).
 * `@cms/contracts` still describes the older, smaller shape; these extend it
 * here, with the fields the screens read.
 */
export type PageStatus = 'draft' | 'review' | 'published' | 'trashed'

export interface PageSeoDto {
  metaTitle?: string
  metaDescription?: string
  ogImage?: string
  canonical?: string
  noindex?: boolean
}

interface PageLifecycleFields {
  title: string
  slug: string
  parentId: string | null
  locale: string
  status: PageStatus
  /** When the live revision goes public; a future date is a scheduled publication. */
  publishAt: number | null
  publishedAt: number | null
  hasUnpublishedChanges: boolean
  translationGroup: string | null
}

export type PageSummaryDto = PageSummary & PageLifecycleFields & { mainImage?: MainImageRef | null }

export interface PageDetailDto extends PageDetail, PageLifecycleFields {
  mainImage?: MainImageRef | null
  seo: PageSeoDto
  previousPaths: string[]
  /** The revision readers see; null when the page was never published. */
  live: { version: number; title: string; path: string } | null
}

export interface ListSitePagesQuery {
  status?: PageStatus
  q?: string
  limit?: number
  cursor?: string
}

export function listSitePages(siteId: string, query: ListSitePagesQuery = {}): Promise<Page<PageSummaryDto>> {
  return apiFetch(`/sites/${siteId}/pages`, { query: { limit: 100, ...query } })
}

/**
 * Every page of the filter, following the gateway's cursor. The tree needs
 * the whole set (a child can sit on a later page than its parent) and a site
 * has tens of pages, not thousands; the cap keeps a runaway loop finite.
 */
export async function listAllSitePages(siteId: string, query: Omit<ListSitePagesQuery, 'cursor' | 'limit'> = {}): Promise<PageSummaryDto[]> {
  const items: PageSummaryDto[] = []
  let cursor: string | undefined
  for (let round = 0; round < 20; round += 1) {
    const result = await listSitePages(siteId, { ...query, cursor })
    items.push(...result.items)
    if (!result.cursor) break
    cursor = result.cursor
  }
  return items
}

export function getPage(pageId: string): Promise<PageDetailDto> {
  return apiFetch(`/pages/${pageId}`)
}

export function createPage(
  siteId: string,
  body: WriteEnvelope & { title: string; slug: string; parentId: string | null },
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/sites/${siteId}/pages`, { method: 'POST', body, idempotencyKey })
}

export function renamePage(pageId: string, body: WriteEnvelope & { title: string }, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/pages/${pageId}/title`, { method: 'PATCH', body, idempotencyKey })
}

/** A `null` value clears that SEO field. */
export function changePageSeo(
  pageId: string,
  body: WriteEnvelope & { seo: Record<string, string | boolean | null> },
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/pages/${pageId}/seo`, { method: 'PATCH', body, idempotencyKey })
}

export function movePage(
  pageId: string,
  body: WriteEnvelope & { parentId: string | null; slug: string },
  idempotencyKey: string,
): Promise<WriteResult & { movedChildren?: number }> {
  return apiFetch(`/pages/${pageId}/move`, { method: 'POST', body, idempotencyKey })
}

export type PageCommand = 'submit' | 'return' | 'publish' | 'unpublish' | 'discard' | 'trash' | 'restore'

/** The lifecycle steps; only `publish` takes more than the envelope (`publishAt`, ISO 8601). */
export function runPageCommand(
  pageId: string,
  command: PageCommand,
  body: WriteEnvelope & { publishAt?: string },
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/pages/${pageId}/${command}`, { method: 'POST', body, idempotencyKey })
}

/** `POST /pages/{id}/deploy`: publishes the page in the CMS if needed and sends only it online (202). */
export function deployPage(pageId: string, body: WriteEnvelope, idempotencyKey: string): Promise<{ page: { id: string; version: number }; publicationId: string }> {
  return apiFetch(`/pages/${pageId}/deploy`, { method: 'POST', body, idempotencyKey })
}

export function getPageHistory(pageId: string, limit?: number): Promise<RecordedEventDto[]> {
  return apiFetch(`/pages/${pageId}/history`, { query: { limit } })
}

export function replacePageLayout(pageId: string, body: WriteEnvelope & { sections: PageSectionNode[] }, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/pages/${pageId}/layout`, { method: 'PUT', body, idempotencyKey })
}

export function setPageLayoutField(pageId: string, body: WriteEnvelope & { sectionId: string; path: string; value: unknown }, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/pages/${pageId}/layout/field`, { method: 'PATCH', body, idempotencyKey })
}

export function addPageSection(pageId: string, body: WriteEnvelope & PageSectionNode, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/pages/${pageId}/sections`, { method: 'POST', body, idempotencyKey })
}

export function movePageSection(pageId: string, sectionId: string, body: WriteEnvelope & { parentId: string | null; index: number | null }, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/pages/${pageId}/sections/${sectionId}/move`, { method: 'POST', body, idempotencyKey })
}

export function removePageSection(pageId: string, sectionId: string, body: WriteEnvelope, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/pages/${pageId}/sections/${sectionId}`, { method: 'DELETE', body, idempotencyKey })
}

export function pagePreviewUrl(pageId: string, mode: 'edit' | 'publish' = 'edit'): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? 'https://api-cms.miglioresoftware.com'
  return `${base}/pages/${encodeURIComponent(pageId)}/preview?mode=${mode}`
}

export function listPublications(siteId: string, status?: PublicationSummary['status'], cursor?: string, limit = 100): Promise<Page<PublicationSummary>> {
  return apiFetch(`/sites/${siteId}/publications`, { query: { status, limit, cursor } })
}

export function rollbackPublication(
  siteId: string,
  targetPublicationId: string,
  body: WriteEnvelope,
  idempotencyKey: string,
): Promise<{ publicationId: string }> {
  return apiFetch(`/sites/${siteId}/publications/${targetPublicationId}/rollback`, { method: 'POST', body, idempotencyKey })
}

export function provisionSite(
  body: WriteEnvelope & {
    tenant: { name: string }
    project: { name: string }
    site: { domain: string; locales: string[]; defaultLocale: string }
    brand?: { accent?: string; fontFamily?: string }
    contentTypes?: string[]
    pages?: { slug: string; title: string; template: string }[]
    menus?: { main?: string[] }
  },
  idempotencyKey: string,
): Promise<ProvisionResultDto> {
  return apiFetch('/provision', { method: 'POST', body, idempotencyKey })
}

export function listContentTypes(): Promise<ContentTypeDefinition[]> {
  return apiFetch('/content-types')
}

export interface ListContentsQuery {
  siteId?: string
  contentTypeKey?: string
  status?: ContentSummary['status']
  search?: string
  limit?: number
  cursor?: string
}

export function listContents(query: ListContentsQuery): Promise<Page<ContentSummary>> {
  return apiFetch('/contents', { query: query as Record<string, string | number | undefined> })
}

export function getContent(id: string, upTo?: number): Promise<ContentDetail> {
  return apiFetch(`/contents/${id}`, { query: { upTo } })
}

export function getContentHistory(id: string, limit?: number): Promise<RecordedEventDto[]> {
  return apiFetch(`/contents/${id}/history`, { query: { limit } })
}

export function createContent(
  body: WriteEnvelope & {
    siteId: string
    contentTypeKey: string
    locale: string
    slug: string
    data: Record<string, unknown>
  },
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch('/contents', { method: 'POST', body, idempotencyKey })
}

export function setContentField(
  id: string,
  body: WriteEnvelope & { path: string; value: unknown },
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/contents/${id}/field`, { method: 'PATCH', body, idempotencyKey })
}

export function changeContentStatus(
  id: string,
  body: WriteEnvelope & { status: ContentSummary['status'] },
  idempotencyKey: string,
): Promise<WriteResult> {
  return apiFetch(`/contents/${id}/status`, { method: 'POST', body, idempotencyKey })
}

export function trashContent(id: string, body: WriteEnvelope, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/contents/${id}/trash`, { method: 'POST', body, idempotencyKey })
}

export function restoreContent(id: string, body: WriteEnvelope, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/contents/${id}/restore`, { method: 'POST', body, idempotencyKey })
}

export function publishSite(
  siteId: string,
  body: WriteEnvelope & { contentIds?: string[] },
  idempotencyKey: string,
): Promise<{ publicationId: string }> {
  return apiFetch(`/sites/${siteId}/publish`, { method: 'POST', body, idempotencyKey })
}

export function getPublicationStatus(siteId: string, publicationId: string): Promise<PublicationStatus> {
  return apiFetch(`/sites/${siteId}/publications/${publicationId}`)
}

export interface AiAccountOption {
  id: string
  label: string
  provider: string
  status: string
  reason?: string | null
  purposes?: string[]
}

export function listAiAccounts(purpose: 'write' | 'review'): Promise<{ accounts: AiAccountOption[] }> {
  return apiFetch('/ai/accounts', { query: { purpose } })
}

export function generateContentField(
  id: string,
  body: WriteEnvelope & {
    accountId: string
    purpose: 'write' | 'review'
    path: string
    prompt: string
  },
  idempotencyKey: string,
): Promise<WriteResult & { text: string; accountUsed: string }> {
  return apiFetch(`/contents/${id}/generate`, { method: 'POST', body, idempotencyKey })
}
